/**
 * Region fan-out shared by the detail and search slices. A single-region query
 * is a fan-out over one base, so both produce one shape and slices never branch.
 *
 * A region holding nothing is not a failure: the APIs answer 200 with the id in
 * `notFound`, or an empty list. Only transport and server errors reject, and
 * those stay in a separate channel so a region being down never reads as absence.
 */
import { getApiBases, isGlobalRegion } from '../../constants';

export interface RegionBase {
  region: string;
  apiBase: string;
}

/** One region that could not be reached, or that errored. */
export interface RegionFailure {
  region: string;
  message: string;
}

interface EnvSlice {
  stage: string;
  region: string;
  apiBase: string;
  global: boolean;
}

/** Every region for a global query, or just the selected one. */
export const regionBases = (env: EnvSlice): RegionBase[] =>
  (env.global || isGlobalRegion(env.region)
    ? getApiBases(env.stage)
    : [{ region: env.region, apiBase: env.apiBase }]);

/** "us-west-2: timed out; eu-west-1: API error 503" — for the error surface. */
export const describeFailures = (errors: RegionFailure[]): string =>
  errors.map(e => `${e.region}: ${e.message}`).join('; ');

type Settled<T> =
  | { region: string; failed: false; value: T }
  | { region: string; failed: true; message: string };

/**
 * Calls every base in parallel, splitting outcomes. Each call catches its own
 * rejection so one bad region cannot blank the others. (Promise.allSettled says
 * this directly but is ES2020; this package's TypeScript lib predates it.)
 */
const settleByRegion = async <T>(
  bases: RegionBase[],
  call: (apiBase: string) => Promise<T>,
): Promise<{ answered: { region: string; value: T }[]; errors: RegionFailure[] }> => {
  const settled = await Promise.all(bases.map((base): Promise<Settled<T>> => call(base.apiBase)
    .then(value => ({ region: base.region, failed: false as const, value }))
    .catch((err: any) => ({
      region: base.region,
      failed: true as const,
      message: err?.message || 'Request failed',
    }))));

  const answered: { region: string; value: T }[] = [];
  const errors: RegionFailure[] = [];
  settled.forEach(outcome => {
    if (outcome.failed) errors.push({ region: outcome.region, message: outcome.message });
    else answered.push({ region: outcome.region, value: outcome.value });
  });
  return { answered, errors };
};

/* ------------------------------------------------------------------ */
/* Detail lookups — one entity, found in one region or several         */
/* ------------------------------------------------------------------ */

/** One region that held the entity. */
export interface RegionHit<T> {
  region: string;
  detail: T;
  /** Further ids the same identifier matched there (e.g. other legs). */
  otherIds: string[];
}

/** What one region's lookup yields: the mapped entity, or null when absent. */
export interface RegionLookupOutcome<T> {
  detail: T | null;
  otherIds: string[];
}

export interface FanOutResult<T> {
  hits: RegionHit<T>[];
  /** Every region queried, so the UI can say what "not found" was measured over. */
  searchedRegions: string[];
  errors: RegionFailure[];
}

/** The region portion of a detail slice's state; identical across entities. */
export interface RegionLookupState<T> {
  hits: RegionHit<T>[];
  /** Region whose hit is currently in `data`. */
  activeRegion: string | null;
  searchedRegions: string[];
  regionErrors: RegionFailure[];
}

export const regionLookupInitialState = {
  hits: [],
  activeRegion: null,
  searchedRegions: [],
  regionErrors: [],
};

/** Looks the entity up in every base, keeping the regions that held it. */
export const fanOutByRegion = async <T>(
  bases: RegionBase[],
  lookup: (apiBase: string) => Promise<RegionLookupOutcome<T>>,
): Promise<FanOutResult<T>> => {
  const { answered, errors } = await settleByRegion(bases, lookup);
  const hits = answered.reduce<RegionHit<T>[]>((found, { region, value }) => (
    value.detail
      ? found.concat({ region, detail: value.detail, otherIds: value.otherIds })
      : found
  ), []);
  return { hits, searchedRegions: bases.map(base => base.region), errors };
};

/* ------------------------------------------------------------------ */
/* Searches — one page per region, never merged                        */
/* ------------------------------------------------------------------ */

/** One region's page of search results. */
export interface RegionPage<TRow> {
  region: string;
  items: TRow[];
  /** That region's server-side total, which may exceed items.length. */
  total: number;
}

export interface SearchFanOutResult<TRow> {
  /** Every region that answered, including those with no matches. */
  regionResults: RegionPage<TRow>[];
  searchedRegions: string[];
  errors: RegionFailure[];
}

/** The region portion of a search slice's state; identical across entities. */
export interface RegionSearchState<TRow> {
  regionResults: RegionPage<TRow>[];
  searchedRegions: string[];
  regionErrors: RegionFailure[];
}

export const regionSearchInitialState = {
  regionResults: [],
  searchedRegions: [],
  regionErrors: [],
};

/**
 * Searches every base, keeping each region's page separate — the regions
 * paginate independently, so a merged page two lines up with nothing. Empty
 * regions are kept so the UI can tell "no matches" from "never asked".
 */
export const fanOutSearch = async <TRow>(
  bases: RegionBase[],
  run: (apiBase: string) => Promise<{ items: TRow[]; total: number }>,
): Promise<SearchFanOutResult<TRow>> => {
  const { answered, errors } = await settleByRegion(bases, run);
  return {
    regionResults: answered.map(({ region, value }) => ({ region, ...value })),
    searchedRegions: bases.map(base => base.region),
    errors,
  };
};

/** Flattens the per-region pages into one list, for single-region consumers. */
export const flattenPages = <TRow>(pages: RegionPage<TRow>[]): { items: TRow[]; total: number } => ({
  items: pages.reduce<TRow[]>((all, page) => all.concat(page.items), []),
  total: pages.reduce((sum, page) => sum + page.total, 0),
});
